import { BottomSheetModal } from '@gorhom/bottom-sheet';
import getFinalPlanImage from 'assets/images/payLater/how-it-works-final-plan.png';
import payWithDebitCardImage from 'assets/images/payLater/how-it-works-pay-with-debit-card.png';
import planPurchaseImage from 'assets/images/payLater/how-it-works-plan-purchase.png';
import { NativeStackHeaderProps, Redirect, Stack } from 'expo-router';
import PurchaseRestrictionsBottomSheet from 'features/payLater/components/PurchaseRestrictionsBottomSheet/PurchaseRestrictionsBottomSheet';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import InfoCarousel, {
  InfoCarouselStep,
} from 'shared/components/InfoCarousel/InfoCarousel';
import Button from 'shared/components/vxl/Button/Button';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import HyperlinkSmText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkSmText';
import useIsFeatureEnabled from 'shared/hooks/statsig/useIsFeatureEnabled/useIsFeatureEnabled';
import { FeatureFlagKey } from 'shared/providers/statsig/StatsigProvider.d';
import theme from 'shared/styles/theme';

const HowItWorks = () => {
  const isPayLaterEnabled = useIsFeatureEnabled(
    FeatureFlagKey.ENABLE_PAY_LATER,
  );
  const { t: translatedText } = useTranslation('payLater', {
    keyPrefix: 'howItWorks',
  });
  const { bottom } = useSafeAreaInsets();
  const purchaseRestrictionsSheetRef = useRef<BottomSheetModal>(null);

  const handlePurchaseRestrictionsPress = () => {
    purchaseRestrictionsSheetRef.current?.present();
  };

  const steps: InfoCarouselStep[] = [
    {
      key: 'planPurchase',
      title: translatedText('steps.planPurchase.title'),
      body: translatedText('steps.planPurchase.body'),
      image: planPurchaseImage,
      imageBackgroundColor: theme.color.expressive.lavender,
      footer: (
        <BodySmText numberOfLines={0} style={styles.secondaryText}>
          {translatedText('purchaseRestrictions.body')}{' '}
          <HyperlinkSmText onPress={handlePurchaseRestrictionsPress}>
            {translatedText('purchaseRestrictions.link')}
          </HyperlinkSmText>
        </BodySmText>
      ),
    },
    {
      key: 'payWithDebitCard',
      title: translatedText('steps.payWithDebitCard.title'),
      body: translatedText('steps.payWithDebitCard.body'),
      image: payWithDebitCardImage,
    },
    {
      key: 'getFinalPlan',
      title: translatedText('steps.getFinalPlan.title'),
      body: translatedText('steps.getFinalPlan.body'),
      image: getFinalPlanImage,
      imageBackgroundColor: theme.color.expressive.seafoam,
    },
  ];

  if (!isPayLaterEnabled) {
    return <Redirect href="/" />;
  }

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          title: translatedText('navigationTitle'),
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      <InfoCarousel steps={steps} />
      <View
        style={[
          styles.buttonContainer,
          { paddingBottom: bottom + theme.spacing.m },
        ]}
      >
        <Button label={translatedText('setUpPaymentPlan')} />
      </View>
      <PurchaseRestrictionsBottomSheet ref={purchaseRestrictionsSheetRef} />
    </View>
  );
};

const styles = StyleSheet.create({
  buttonContainer: {
    padding: theme.spacing.m,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  secondaryText: {
    color: theme.color.text.secondary,
  },
});

export default HowItWorks;
